/* HOW IS THIS CHAMPION DECIDED — by an EVENT, or by STANDINGS?
 *
 * Brian, from the Zipcio call: "SOCon football is decided by standings only.
 * They don't have a championship that happens… if we are solely relying on a
 * championship date to trigger search, we won't find a champion. Because it
 * doesn't exist on a date, so it's not gonna query a no-champion-found because
 * it never searches for it."
 *
 * ⚠️ THIS IS THE SILENT-ZERO PATTERN IN THE PLACE IT COSTS MOST. Every other
 * failure in this system at least reports itself: a hunt that finds nothing
 * says so, a roster that comes back short is flagged. A championship with no
 * DATE is never even attempted — the sweep looks for events in a window, this
 * record has no event, so it is not in any window, so nothing ever asks about
 * it and nothing ever complains. It is not a failed search. It is the absence
 * of a search, which is invisible by construction.
 *
 * ⚠️ AND IT IS NOT A SMALL CATEGORY. Measured live over 6,000 records, 578
 * (9.6%) carry no event date. Most FCS and many D2/D3 conferences stage NO
 * football championship game at all — the regular-season standings decide it.
 * The Ivy League has never held one. So "no date" is currently doing double
 * duty for two opposite things:
 *
 *     DATE MISSING     — a real event whose date we have not captured.
 *                        A gap. Fix by finding the date.
 *     NO EVENT EXISTS  — the title is decided by standings at season's end.
 *                        NOT a gap. The record is correct as it stands, and
 *                        flagging it as defective is a false positive that
 *                        buries the real ones.
 *
 * Conflating them means the genuine missing dates drown in noise AND the
 * standings titles never get hunted. Both halves are fixed by knowing which is
 * which.
 *
 * Pure. No React, no fetch.
 */

const S = (v) => String(v == null ? '' : v).trim()
const L = (v) => S(v).toLowerCase()

export const BY_EVENT = 'event'
export const BY_STANDINGS = 'standings'
export const UNKNOWN = 'unknown'

/* =========================================================================
 * CONFERENCES THAT CROWN A FOOTBALL CHAMPION WITHOUT A CHAMPIONSHIP GAME.
 *
 * ⚠️ THIS IS THE NORMAL CASE IN FCS, NOT THE EXCEPTION, which is why treating
 * a missing date as a defect was so wrong. A conference championship game
 * requires divisions or a waiver; most FCS leagues simply play a round-robin
 * and the standings decide it. Several award co-championships as a result —
 * two teams, two rings orders, and no event on any calendar.
 *
 * Sourced from each conference's own championship format. Where a league later
 * ADDED a title game, it is not listed here.
 * ========================================================================= */
const FOOTBALL_BY_STANDINGS = new Set([
  'southern conference', 'socon',
  'ivy league',
  'patriot league',
  'pioneer football league', 'pfl',
  'northeast conference', 'nec',
  'ohio valley conference', 'ovc',
  'big sky conference', 'big sky',
  'missouri valley football conference', 'mvfc',
  'colonial athletic association football', 'caa football', 'caaf',
  'southland conference', 'southland',
  'united athletic conference', 'uac',
  'mid-eastern athletic conference', 'meac',
  'northern sun intercollegiate conference', 'nsic',
  'great american conference', 'gac',
  'great northwest athletic conference',
  'lone star conference', 'lsc',
  'rocky mountain athletic conference', 'rmac',
])

/* Sports that are ALWAYS decided by a season-long table rather than a single
 * event, wherever they are played. Rare, but real. */
const ALWAYS_STANDINGS = new Set([
  'football',   // only when the conference is listed above — handled below
])

/**
 * How is this championship's winner decided?
 *
 * @returns { how, reason, searchable }
 *   how         'event' | 'standings' | 'unknown'
 *   searchable  whether a champion can be hunted for it TODAY. A standings
 *               title IS searchable — the season ends on a known-ish date —
 *               which is the whole point of separating it from a missing date.
 */
export function decidedBy(record = {}) {
  const raw = record?.raw || record || {}
  const conference = L(raw.sr_conference ?? record.conference)
  const sport = L(raw.sr_sport ?? record.sport)
  const name = L(raw.championship_name ?? record.name ?? record.championship)
  const hasDate = Boolean(S(raw.sr_event_date ?? record.eventDate ?? record.effDate))

  /* ⚠️ A DATE WINS THE ARGUMENT. If the record carries an event date then an
   * event exists, whatever any list here says — the data beats the model, and
   * a conference that ADDED a title game should not be overruled by a stale
   * entry in a hardcoded set. */
  if (hasDate) {
    return { how: BY_EVENT, searchable: true, reason: 'The record carries an event date, so a championship event exists.' }
  }

  /* Football in a conference that stages no title game. */
  const isFootball = /\bfootball\b/.test(sport) || /\bfootball\b/.test(name)
  if (isFootball && ALWAYS_STANDINGS.has('football')) {
    for (const c of FOOTBALL_BY_STANDINGS) {
      if (conference && (conference === c || conference.includes(c))) {
        return {
          how: BY_STANDINGS,
          searchable: true,
          reason: `${S(raw.sr_conference ?? record.conference)} stages no football championship game — the regular-season standings decide the title, and co-champions are possible. This record having no event date is CORRECT, not a gap.`,
        }
      }
    }
  }

  /* ⚠️ EVERYTHING ELSE WITH NO DATE IS GENUINELY UNKNOWN, and saying so is the
   * point. Returning 'standings' for anything undated would silence the real
   * missing dates — a CIF or NCHSAA state final absolutely does happen on a
   * day, and its blank date is a gap somebody has to close. */
  return {
    how: UNKNOWN,
    searchable: false,
    reason: 'No event date, and this is not a conference known to decide the title on standings — so the date is missing rather than absent by design.',
  }
}

/** Is this record's missing date CORRECT rather than a defect? */
export function noDateIsExpected(record) {
  return decidedBy(record).how === BY_STANDINGS
}

/**
 * ⚠️ THE SEARCH WINDOW FOR A STANDINGS TITLE. It has no event date, so a
 * date-windowed sweep will never include it unless something supplies one.
 * A college football regular season ends in late November, so the champion is
 * known by roughly the first week of December.
 *
 * This is deliberately a SEARCH window, not a write to `sr_event_date` — the
 * record correctly has no event, and stamping a fake date onto it to make the
 * sweep work would be inventing an event that does not exist. The sweep asks
 * this function; the record stays honest.
 */
export function searchWindowFor(record, year = null) {
  const d = decidedBy(record)
  if (d.how !== BY_STANDINGS) return null
  const raw = record?.raw || record || {}
  const y = Number(year ?? raw.sr_season_year ?? new Date().getFullYear())
  if (!Number.isFinite(y)) return null
  return {
    /* Late November through mid-December — the season ends and the table is
     * final. Wide on purpose: the exact final weekend moves year to year. */
    from: `${y}-11-20`,
    to: `${y}-12-15`,
    reason: 'Decided by final regular-season standings; searchable once the season ends.',
  }
}

export const STANDINGS_CONFERENCE_COUNT = FOOTBALL_BY_STANDINGS.size
